import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../store";
import { getSessionAttribution, getSegmentForProduct } from "../utils/sessionAttribution";
import { APIRequestStatus } from "common-ts/dist/models/APIRequestStatus";

// As always
import { enableMapSet } from "immer";
enableMapSet();

interface ProductAnalyticsState {
  productViewMap: Map<string, string>;
  productVisitMap: Map<string, string>;
  productSelectSizeMap: Map<string, string>;
  productAddToCartMap: Map<string, string>;
  productCheckoutMap: Map<string, string>;
}

const loadMapFromLocalStorage = (key: string): Map<string, string> => {
  const storedData = localStorage.getItem(key);
  return storedData ? new Map(JSON.parse(storedData)) : new Map(); // Buat baru kalau tidak ada
};

const saveMapToLocalStorage = (key: string, map: Map<string, string>) => {
  localStorage.setItem(key, JSON.stringify(Array.from(map.entries())));
};

const initialState: ProductAnalyticsState = {
  productViewMap: loadMapFromLocalStorage('productViewMap'),
  productVisitMap: loadMapFromLocalStorage('productVisitMap'),
  productSelectSizeMap: loadMapFromLocalStorage('productSelectSizeMap'),
  productAddToCartMap: loadMapFromLocalStorage('productAddToCartMap'),
  productCheckoutMap: loadMapFromLocalStorage('productCheckoutMap'),
};

interface ProductAnalyticsPayload {
  event: string;
  isMobileView: boolean;
  productId: string;
  origin?: string;
  positionY?: number; // document-Y offset (px) of the product card on VIEW events
}

const EVENT_TYPES = {
  VIEW: "VIEW",
  VISIT: "VISIT",
  SELECT_SIZE: "SELECT-SIZE",
  ADD_TO_CART: "ADD-TO-CART",
  CHECKOUT: "CHECKOUT",
  PURCHASE: "PURCHASE",
};

const TIMEOUT = 2 * 60 * 60 * 1000;

export const postNewProductAnalyticsLog = createAsyncThunk(
  "productAnalyticsLog/postNewProductAnalyticsLog",
  async (payload: ProductAnalyticsPayload, thunkAPI) => {
    const { productId, event, isMobileView, origin, positionY } = payload;
    const rootState = thunkAPI.getState() as RootState;
    const currentState = rootState.productAnalyticsLog;
    const userSub = rootState.account.sub || rootState.account.distinctUserId || "GUEST";
    const currentProductId = productId;

    // Guard the literal string 'undefined' too — some mount paths interpolate a missing
    // route param into the productId (observed as "VISIT ON undefined" in production).
    if (!currentProductId || currentProductId === 'undefined') return;

    const currentTimestamp = new Date().toISOString();
    let eventMap: Map<string, string> | null = null;
  
    switch (event) {
      case EVENT_TYPES.VIEW:
        eventMap = currentState.productViewMap;
        break;
      case EVENT_TYPES.VISIT:
        eventMap = currentState.productVisitMap;
        break;
      case EVENT_TYPES.SELECT_SIZE:
        eventMap = currentState.productSelectSizeMap;
        break;
      case EVENT_TYPES.ADD_TO_CART:
        eventMap = currentState.productAddToCartMap;
        break;
      case EVENT_TYPES.CHECKOUT:
        eventMap = currentState.productCheckoutMap;
        break;
      default:
        return;
    }
  
    const lastTimestamp = eventMap?.get(currentProductId);
    const shouldPostAnalytics = !lastTimestamp || new Date(currentTimestamp).getTime() - new Date(lastTimestamp).getTime() > TIMEOUT
  
    switch (event) {
      case EVENT_TYPES.VIEW:
        thunkAPI.dispatch(updateProductViewMap({ currentProductId, timestamp: currentTimestamp }));
        break;
      case EVENT_TYPES.VISIT:
        thunkAPI.dispatch(updateProductVisitMap({ currentProductId, timestamp: currentTimestamp }));
        break;
      case EVENT_TYPES.SELECT_SIZE:
        thunkAPI.dispatch(updateProductSelectSizeMap({ currentProductId, timestamp: currentTimestamp }));
        break;
      case EVENT_TYPES.ADD_TO_CART:
        thunkAPI.dispatch(updateProductAddToCartMap({ currentProductId, timestamp: currentTimestamp }));
        break;
      case EVENT_TYPES.CHECKOUT:
        thunkAPI.dispatch(updateProductCheckoutMap({ currentProductId, timestamp: currentTimestamp }));
        break;
    }

    if (shouldPostAnalytics) {
      // Session attribution: which stratum this event belongs to for product scoring.
      // AD-TARGETED = this session landed on an ad for a collection containing this product
      // (or directly on this product's PDP); everything else is ORGANIC.
      const attribution = getSessionAttribution();
      const segment = getSegmentForProduct(
        currentProductId,
        rootState.productsList.collectionsDictionary
      );

      // Stock state at event time (stock-availability de-biasing). Only trusted once the
      // availability dict has actually loaded — before that, every product transiently
      // reads as sold out (empty dict), which would mislabel events.
      let stockLeft: number | undefined = undefined;
      let availMask: number | undefined = undefined;
      if (rootState.productsList.statusSizeAvailability === APIRequestStatus.Success) {
        const availability = rootState.productsList.productSizeAvailabilityDict?.[currentProductId];
        if (availability) {
          if (typeof availability.numberOfItemsRemaining === 'number') {
            stockLeft = Math.max(0, Math.round(availability.numberOfItemsRemaining));
          }
          if (Array.isArray(availability.availabilityArray)) {
            // 12-bit mask: regular XXS..XXL at bits 0-6, plus 3XL..7XL at 7-11. Raw booleans —
            // the demand-weight table lives in the score worker so it stays retunable.
            availMask = 0;
            for (let i = 0; i < 12; i++) {
              if (availability.availabilityArray[i]) availMask |= 1 << i;
            }
          }
        }
      }

      const data = {
        productId: currentProductId,
        timestamp: currentTimestamp,
        userSub,
        isMobileView,
        event,
        origin,
        sessionId: attribution.sessionId,
        segment,
        adSource: attribution.adSource,
        adCollectionKey: attribution.adCollectionKey,
        positionY: event === EVENT_TYPES.VIEW && typeof positionY === 'number' && positionY >= 0
          ? positionY : undefined,
        stockLeft,
        availMask,
      };

      console.log('ANALYTICS EVENT: ' + data.event + ' ON ' + data.productId)

      // Fire-and-forget beacon: swallow failures so a network blip / adblocker / endpoint 5xx
      // doesn't surface as an unhandled promise rejection. Note the event is still lost on
      // failure (the dedup map was already stamped above), but analytics loss must never
      // affect the shopping flow — losses are ~random across products so ratios stay valid.
      axios({
        method: "post",
        url: "https://ck8k2pv7c9.execute-api.ap-southeast-1.amazonaws.com/ProductAPIProduction/product-analytics",
        data: JSON.stringify(data)
      }).catch(() => {});
    }
  }
);


export const productAnalyticsLogSlice = createSlice({
  name: "productAnalyticsLog",
  initialState,
  reducers: {
    updateProductViewMap: (
      state,
      action: PayloadAction<{ currentProductId: string; timestamp: string }>
    ) => {
      state.productViewMap.set(
        action.payload.currentProductId,
        action.payload.timestamp
      );
      saveMapToLocalStorage('productViewMap', state.productViewMap);
    },
    updateProductVisitMap: (
      state,
      action: PayloadAction<{ currentProductId: string; timestamp: string }>
    ) => {
      state.productVisitMap.set(
        action.payload.currentProductId,
        action.payload.timestamp
      );
      saveMapToLocalStorage('productVisitMap', state.productVisitMap);
    },
    updateProductSelectSizeMap: (
      state,
      action: PayloadAction<{ currentProductId: string; timestamp: string }>
    ) => {
      state.productSelectSizeMap.set(
        action.payload.currentProductId,
        action.payload.timestamp
      );
      saveMapToLocalStorage('productSelectSizeMap', state.productSelectSizeMap);
    },
    updateProductAddToCartMap: (
      state,
      action: PayloadAction<{ currentProductId: string; timestamp: string }>
    ) => {
      state.productAddToCartMap.set(
        action.payload.currentProductId,
        action.payload.timestamp
      );
      saveMapToLocalStorage('productAddToCartMap', state.productAddToCartMap);
    },
    updateProductCheckoutMap: (
      state,
      action: PayloadAction<{ currentProductId: string; timestamp: string }>
    ) => {
      state.productCheckoutMap.set(
        action.payload.currentProductId,
        action.payload.timestamp
      );
      saveMapToLocalStorage('productCheckoutMap', state.productCheckoutMap);
    },
  },
  extraReducers(builder) {
    builder.addCase(
      postNewProductAnalyticsLog.fulfilled,
      (state, action) => {}
    );
  },
});

export const {
  updateProductViewMap,
  updateProductVisitMap,
  updateProductSelectSizeMap,
  updateProductAddToCartMap,
  updateProductCheckoutMap,
} = productAnalyticsLogSlice.actions;

export default productAnalyticsLogSlice.reducer;